/**
 * LiveStreamDemo
 * Real-time analysis with live overlay - Modern design with Lucide icons
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RecordRTC from "recordrtc";
import { JobType } from "../../types";
import { useCameraAccess } from "../../hooks/useCameraAccess";
import { useFrameCapture } from "../../hooks/useFrameCapture";
import { liveAnalysisService } from "../../services/liveAnalysis";
import type { AnalysisResult } from "../../types";
import { useAppDispatch, jobsActions, uiActions } from "../../redux";
import {
  Play,
  Square,
  Video,
  Target,
  AlertCircle,
  AlertTriangle,
  Clock,
  BarChart3,
  Lightbulb,
} from "lucide-react";

interface LiveStreamDemoProps {
  jobType: JobType;
}

interface SessionAnalysisFrame {
  timestamp: number;
  frameNumber: number;
  results: AnalysisResult;
}

interface Detection {
  x: number;
  y: number;
  width: number;
  height: number;
  confidence?: number;
  label?: string;
}

export const LiveStreamDemo: React.FC<LiveStreamDemoProps> = ({ jobType }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { videoRef, isActive, error, startCamera, stopCamera } =
    useCameraAccess();
  const { extractFrame } = useFrameCapture();

  // Canvas for drawing analyzed output with bounding boxes
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false); // NEW: Track recording state separately
  const [results, setResults] = useState<AnalysisResult | null>(null);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [frameCount, setFrameCount] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [sessionDuration, setSessionDuration] = useState<number>(0);
  const [sessionAnalysisData, setSessionAnalysisData] = useState<
    SessionAnalysisFrame[]
  >([]);

  // Refs to avoid stale closures
  const streamIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const animationFrameRef = useRef<number | null>(null);
  const isAnalyzingRef = useRef<boolean>(false);
  const isStreamingRef = useRef<boolean>(false);
  const isRecordingRef = useRef<boolean>(false); // NEW: Ref for recording state
  const hasFirstAnalysisRef = useRef<boolean>(false); // NEW: Track if first analysis is complete
  const latestResultsRef = useRef<AnalysisResult | null>(null);
  const recorderRef = useRef<RecordRTC | null>(null);

  // Additional refs for duration and frame count
  const sessionDurationRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);

  // Start camera on mount
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      cleanupSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update refs when state changes
  useEffect(() => {
    latestResultsRef.current = results;
  }, [results]);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    sessionDurationRef.current = sessionDuration;
  }, [sessionDuration]);

  useEffect(() => {
    frameCountRef.current = frameCount;
  }, [frameCount]);

  // Cleanup function
  const cleanupSession = (): void => {
    if (streamIntervalRef.current) {
      clearInterval(streamIntervalRef.current);
      streamIntervalRef.current = null;
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    liveAnalysisService.cancelPendingAnalysis();
  };

  // Clamp value within bounds
  const clamp = (value: number, min: number, max: number): number => {
    return Math.max(min, Math.min(max, value));
  };

  // Robust bounding box drawing with proper clamping
  const drawBoundingBoxes = (
    ctx: CanvasRenderingContext2D,
    detections: Detection[],
    canvasWidth: number,
    canvasHeight: number
  ): void => {
    detections.forEach((detection, index) => {
      let { x, y, width, height } = detection;

      // Handle normalized coordinates (0-1 range)
      if (x <= 1 && y <= 1 && width <= 1 && height <= 1) {
        x *= canvasWidth;
        y *= canvasHeight;
        width *= canvasWidth;
        height *= canvasHeight;
      }

      // Clamp coordinates to stay within canvas bounds
      const edgePadding = 4;
      const minX = clamp(x, edgePadding, canvasWidth - edgePadding);
      const minY = clamp(y, edgePadding, canvasHeight - edgePadding);

      // Ensure width and height don't extend beyond canvas
      const maxWidth = canvasWidth - minX - edgePadding;
      const maxHeight = canvasHeight - minY - edgePadding;

      const clampedWidth = clamp(width, 1, maxWidth);
      const clampedHeight = clamp(height, 1, maxHeight);

      // Draw bounding box
      ctx.strokeStyle = "#00FFFF";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.rect(minX, minY, clampedWidth, clampedHeight);
      ctx.stroke();

      // Draw Label
      const label = detection.label || `Object ${index + 1}`;
      const confidence = detection.confidence
        ? ` ${(detection.confidence * 100).toFixed(0)}%`
        : "";
      const labelText = `${label}${confidence}`;

      ctx.font = "bold 14px Arial";
      const textMetrics = ctx.measureText(labelText);
      const labelHeight = 22;
      const labelPadding = 6;
      const labelWidth = textMetrics.width + labelPadding * 2;

      // Position label inside the bounding box at top-left corner
      let labelX = minX + 2;
      let labelY = minY + 2;

      // Ensure label stays within canvas
      if (labelX + labelWidth > canvasWidth - edgePadding) {
        labelX = canvasWidth - labelWidth - edgePadding;
      }
      if (labelY + labelHeight > canvasHeight - edgePadding) {
        labelY = canvasHeight - labelHeight - edgePadding;
      }

      // Draw label background
      ctx.fillStyle = "rgba(0, 255, 255, 0.9)";
      ctx.fillRect(labelX, labelY, labelWidth, labelHeight);

      // Draw label text
      ctx.fillStyle = "#000000";
      ctx.fillText(labelText, labelX + labelPadding, labelY + 16);
    });
  };

  // Canvas rendering loop with strict dimension sync
  const renderCanvasFrame = (): void => {
    if (!isStreamingRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || video.readyState < 2) {
      animationFrameRef.current = requestAnimationFrame(renderCanvasFrame);
      return;
    }

    // Ensure canvas matches video resolution exactly
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      if (
        canvas.width !== video.videoWidth ||
        canvas.height !== video.videoHeight
      ) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }
    }

    const ctx = canvas.getContext("2d");
    if (ctx) {
      const currentResults = latestResultsRef.current;

      // Only render analyzed output if we have results
      if (currentResults?.results) {
        // 1. Draw Video
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // 2. Draw Bounding Boxes
        if (currentResults.results.detections?.length) {
          drawBoundingBoxes(
            ctx,
            currentResults.results.detections as Detection[],
            canvas.width,
            canvas.height
          );
        }

        // 3. Draw Overlays (Count & Stats)
        const count = currentResults.results.count ?? 0;
        drawCountOverlay(
          ctx,
          count,
          sessionDurationRef.current,
          frameCountRef.current
        );
      }
    }

    animationFrameRef.current = requestAnimationFrame(renderCanvasFrame);
  };

  // Draw count overlay on canvas - properly inside bounds
  const drawCountOverlay = (
    ctx: CanvasRenderingContext2D,
    count: number,
    duration: number,
    frames: number
  ): void => {
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    const padding = 15;

    // Count box (BOTTOM-LEFT) - inside canvas
    const countBoxWidth = 100;
    const countBoxHeight = 70;
    const countBoxX = padding;
    const countBoxY = canvasHeight - countBoxHeight - padding;

    ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
    ctx.fillRect(countBoxX, countBoxY, countBoxWidth, countBoxHeight);

    ctx.strokeStyle = "#00FFFF";
    ctx.lineWidth = 2;
    ctx.strokeRect(countBoxX, countBoxY, countBoxWidth, countBoxHeight);

    ctx.fillStyle = "#00FFFF";
    ctx.font = "bold 36px Arial";
    ctx.textAlign = "center";
    ctx.fillText(String(count), countBoxX + countBoxWidth / 2, countBoxY + 42);

    ctx.font = "12px Arial";
    ctx.fillStyle = "#AAAAAA";
    ctx.fillText("Detected", countBoxX + countBoxWidth / 2, countBoxY + 60);

    // Stats box (BOTTOM-RIGHT) - inside canvas
    const statsBoxWidth = 110;
    const statsBoxHeight = 55;
    const statsX = canvasWidth - statsBoxWidth - padding;
    const statsY = canvasHeight - statsBoxHeight - padding;

    ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
    ctx.fillRect(statsX, statsY, statsBoxWidth, statsBoxHeight);

    ctx.strokeStyle = "#00FFFF";
    ctx.lineWidth = 2;
    ctx.strokeRect(statsX, statsY, statsBoxWidth, statsBoxHeight);

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "13px Arial";
    ctx.textAlign = "left";
    ctx.fillText(`⏱️ ${duration}s`, statsX + 12, statsY + 22);
    ctx.fillText(`📊 ${frames} frames`, statsX + 12, statsY + 42);

    // Reset text align
    ctx.textAlign = "start";
  };

  // Start RecordRTC recording on canvas - NOW called only after first analysis
  const startRecording = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("❌ Canvas not ready for recording");
      setAnalysisError("Canvas not ready for recording");
      return;
    }

    // Don't start if already recording
    if (recorderRef.current || isRecordingRef.current) {
      console.log("⚠️ Recording already in progress");
      return;
    }

    try {
      const canvasStream = canvas.captureStream(30);
      const recorder = new RecordRTC(canvasStream, {
        type: "video",
        mimeType: "video/webm;codecs=vp8",
        videoBitsPerSecond: 2500000,
        frameRate: 30,
      });

      recorder.startRecording();
      recorderRef.current = recorder;
      setIsRecording(true);
      isRecordingRef.current = true;
      console.log(
        "✅ RecordRTC recording started on canvas (after first analysis)"
      );
    } catch (err) {
      console.error("❌ Failed to start recording:", err);
      setAnalysisError("Failed to start video recording");
    }
  };

  // Stop RecordRTC recording
  const stopRecording = (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const recorder = recorderRef.current;
      if (!recorder) {
        // If no recorder, return empty blob
        console.warn("⚠️ No recorder available, returning empty blob");
        resolve(new Blob([], { type: "video/webm" }));
        return;
      }

      recorder.stopRecording(() => {
        const blob = recorder.getBlob();
        console.log(
          `✅ Recording stopped. Size: ${(blob.size / 1024 / 1024).toFixed(
            2
          )} MB`
        );
        recorderRef.current = null;
        setIsRecording(false);
        isRecordingRef.current = false;
        resolve(blob);
      });
    });
  };

  // Analyze current frame
  const analyzeCurrentFrame = async (): Promise<void> => {
    if (
      isAnalyzingRef.current ||
      !videoRef.current ||
      !isStreamingRef.current
    ) {
      return;
    }

    isAnalyzingRef.current = true;
    setAnalysisError(null);

    try {
      const frame = await extractFrame(videoRef.current);
      if (frame) {
        const analysisResults = await liveAnalysisService.analyzeFrame(
          frame,
          jobType
        );

        setResults(analysisResults);

        const newFrame: SessionAnalysisFrame = {
          timestamp: Date.now(),
          frameNumber: frameCountRef.current + 1,
          results: analysisResults,
        };

        setSessionAnalysisData((prev) => [...prev, newFrame]);
        setFrameCount((prev) => prev + 1);

        // START RECORDING ONLY AFTER FIRST SUCCESSFUL ANALYSIS
        if (!hasFirstAnalysisRef.current && analysisResults?.results) {
          hasFirstAnalysisRef.current = true;
          console.log("🎬 First analysis complete, starting recording...");

          // Small delay to ensure canvas has rendered the first frame
          setTimeout(() => {
            startRecording();
          }, 100);
        }
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Analysis failed";
      if (
        !errorMessage.includes("cancel") &&
        !errorMessage.includes("cancelled")
      ) {
        console.error("Frame analysis error:", err);
        setAnalysisError(errorMessage);
      }
    } finally {
      isAnalyzingRef.current = false;
    }
  };

  // Start analysis
  const handleStartAnalysis = (): void => {
    if (!isActive || !videoRef.current) {
      setAnalysisError("Camera not ready. Please wait...");
      return;
    }

    setIsStreaming(true);
    isStreamingRef.current = true;
    setIsRecording(false); // Recording starts later
    isRecordingRef.current = false;
    hasFirstAnalysisRef.current = false; // Reset first analysis flag
    setSessionStartTime(Date.now());
    setFrameCount(0);
    frameCountRef.current = 0;
    setResults(null);
    latestResultsRef.current = null;
    setAnalysisError(null);
    setSessionAnalysisData([]);
    setSessionDuration(0);
    sessionDurationRef.current = 0;

    // Start duration timer
    durationIntervalRef.current = setInterval(() => {
      setSessionDuration((prev) => {
        sessionDurationRef.current = prev + 1;
        return prev + 1;
      });
    }, 1000);

    // Start canvas animation loop
    animationFrameRef.current = requestAnimationFrame(renderCanvasFrame);

    // Start analysis loop (recording will start after first successful analysis)
    streamIntervalRef.current = setInterval(() => {
      analyzeCurrentFrame();
    }, 1500);

    // Trigger first analysis
    setTimeout(() => {
      analyzeCurrentFrame();
    }, 300);
  };

  // End session
  const handleEndSession = async (): Promise<void> => {
    setIsStreaming(false);
    isStreamingRef.current = false;
    cleanupSession();
    await handleSaveSession();
  };

  // Save session
  const handleSaveSession = async (): Promise<void> => {
    if (!sessionStartTime || sessionAnalysisData.length === 0) {
      dispatch(
        uiActions.addNotification({
          type: "warning",
          message: "No analysis data to save. Please run the analysis first.",
        })
      );
      return;
    }

    setIsSaving(true);

    try {
      const videoBlob = await stopRecording();

      // Check if we have actual video data
      if (videoBlob.size === 0) {
        dispatch(
          uiActions.addNotification({
            type: "warning",
            message: "No video was recorded. Analysis may not have completed.",
          })
        );
        setIsSaving(false);
        return;
      }

      const file = new File([videoBlob], "live-demo-session.webm", {
        type: "video/webm",
      });

      const sessionData = {
        startTime: sessionStartTime,
        endTime: Date.now(),
        totalFrames: frameCount,
        sessionDuration: sessionDuration,
        analysisResults: sessionAnalysisData.map((frame) => ({
          frameNumber: frame.frameNumber,
          timestamp: frame.timestamp,
          count: frame.results.results?.count || 0,
          detections: frame.results.results?.detections || [],
        })),
        statistics: {
          avgCount:
            sessionAnalysisData.length > 0
              ? sessionAnalysisData.reduce(
                  (sum, frame) => sum + (frame.results.results?.count || 0),
                  0
                ) / sessionAnalysisData.length
              : 0,
          maxCount: Math.max(
            0,
            ...sessionAnalysisData.map(
              (frame) => frame.results.results?.count || 0
            )
          ),
          minCount:
            sessionAnalysisData.length > 0
              ? Math.min(
                  ...sessionAnalysisData.map(
                    (frame) => frame.results.results?.count || 0
                  )
                )
              : 0,
        },
      };

      const job = await dispatch(
        jobsActions.createJob({
          jobType,
          file: file,
          isLiveDemo: true,
          sessionData: sessionData,
        })
      ).unwrap();

      dispatch(
        uiActions.addNotification({
          type: "success",
          message: `Live demo session saved! Analyzed ${frameCount} frames.`,
        })
      );

      navigate(`/jobs/${job.id}`);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to save session";
      console.error("Save session error:", err);
      dispatch(
        uiActions.addNotification({
          type: "error",
          message: errorMessage,
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Glass Card Container */}
      <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 shadow-2xl">
        {error && (
          <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {analysisError && (
          <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-amber-400">{analysisError}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Left: Input Feed */}
          <div className="relative bg-slate-900 rounded-xl overflow-hidden min-h-[400px] border border-slate-700/50">
            <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900/80 backdrop-blur-sm text-sm font-medium text-white border border-slate-700/50">
              <Video className="w-4 h-4 text-primary-400" />
              Input Feed
            </div>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right: Analyzed Output Canvas */}
          <div className="relative bg-slate-900 rounded-xl overflow-hidden min-h-[400px] border border-slate-700/50">
            <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900/80 backdrop-blur-sm text-sm font-medium text-white border border-slate-700/50">
              <Target className="w-4 h-4 text-emerald-400" />
              Analyzed Output
              {isRecording && (
                <span className="flex items-center gap-1.5 ml-2 text-red-400">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  Recording
                </span>
              )}
              {isStreaming && !isRecording && (
                <span className="flex items-center gap-1.5 ml-2 text-amber-400">
                  <Clock className="w-3 h-3" />
                  Waiting...
                </span>
              )}
            </div>
            <canvas
              ref={canvasRef}
              className={`w-full h-full object-contain ${
                !isStreaming || !results ? "hidden" : ""
              }`}
            />
            {!isStreaming && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                <div className="text-center p-8">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center mx-auto mb-4">
                    <Target className="w-10 h-10 text-primary-400" />
                  </div>
                  <p className="text-slate-300 text-lg font-medium mb-2">
                    Analysis output will appear here
                  </p>
                  <p className="text-slate-500 text-sm">
                    {isActive
                      ? 'Click "Start Live Analysis" to begin'
                      : "Waiting for camera..."}
                  </p>
                </div>
              </div>
            )}

            {isStreaming && !results && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm pointer-events-none">
                <div className="text-center p-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-amber-400 animate-pulse" />
                  </div>
                  <p className="text-slate-300 text-lg font-medium mb-2">
                    Analyzing first frame...
                  </p>
                  <p className="text-slate-500 text-sm">
                    Recording will start after analysis
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-4">
          {!isStreaming ? (
            <button
              onClick={handleStartAnalysis}
              disabled={!isActive || isSaving}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 disabled:from-slate-600 disabled:to-slate-700 text-white font-medium rounded-xl transition-all shadow-lg shadow-primary-500/20 disabled:shadow-none"
            >
              <Play className="w-5 h-5" />
              {isActive ? "Start Live Analysis" : "Waiting for camera..."}
            </button>
          ) : (
            <button
              onClick={handleEndSession}
              disabled={isSaving || !isRecording}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 disabled:bg-slate-700 text-white disabled:text-slate-400 font-medium rounded-xl transition-all"
            >
              {isSaving ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Square className="w-5 h-5" />
              )}
              {isSaving
                ? "Saving Video..."
                : !isRecording
                ? "Waiting for Analysis..."
                : "End Session & Save"}
            </button>
          )}
        </div>

        {!isStreaming && (
          <div className="flex items-start gap-3 mt-4 p-4 bg-slate-700/30 rounded-xl border border-slate-600/30">
            <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-400">
              Live analysis runs at ~1 FPS. Recording starts only after the first frame is analyzed. 
              The analyzed video with bounding boxes will be saved when you end the session.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
